import PropTypes from 'prop-types';
import React, {useContext, useRef} from 'react';
import {SortableContainer, SortableElement, SortableHandle} from "react-sortable-hoc";

import FormLayout from './form-layout';
import InputGroup from '../controls/input-group';
import ControlGroup from "../controls/control-group";
import Gallery from "../controls/gallery";
import List from "../controls/list";
import SchemaBindingConfig from "../controls/schema-binding-config";
import DataFilter from "../controls/data-filter";
import Table from "../controls/table";
import Spacer from "../controls/spacer";
import Paragraph from "../controls/paragraph";
import Info from "../controls/info";
import FieldBuilder from "../builder/field-builder";
import {evaluate, getValue} from "../utils";
import FormContext from "../form-context";
import _isObject from "lodash/isObject";
import _isNumber from "lodash/isNumber";
import MenuIcon from '@heroicons/react/solid/MenuIcon'
import classNames from "classnames";
import Block from "../controls/block";
import Tree from "../controls/tree";
import EntityLabeler from "../controls/entity-labeler";
import CategoryField from "../controls/category-field";
import ActionInput from "../controls/action-input";
import VideoInput from "../controls/video-input";
import AvailabilitySlotPicker from "../controls/availability-slot-picker";

const getExtendableSettings = (fieldset, formExtendable) => {
    // Handle when extendable is an object and merge setting together with the form extendable prop
    let extendable;
    if (_isObject(fieldset.extendable) && _isObject(formExtendable)) {
        extendable = {...formExtendable, ...fieldset.extendable};
    } else if (_isObject(fieldset.extendable)) {
        extendable = fieldset.extendable;
    } else {
        extendable = formExtendable;
    }
    return extendable;
}

const isSortableField = (field = {}) => !field.system || field.contactField === true;

const DragHandler = SortableHandle ? SortableHandle(() => (
    <div className="cx-drag-handle shrink-0 flex items-center pr-2 cursor-pointer">
        <MenuIcon className="cx-drag-icon h-5 w-5" aria-hidden="true"/>
    </div>
)) : () => {
    return null
};

function Field({index, field, fieldset, ...props}) {
    const context = useContext(FormContext);
    const extendable = getExtendableSettings(fieldset, props.extendable);

    // Render a form control
    let Control;
    switch (field.type) {
        case 'form-layout':
            Control = FormLayout;
            break;
        case 'list':
            Control = List;
            break;
        case 'schema-binding-config':
            Control = SchemaBindingConfig;
            break;
        case 'data-filter':
            Control = DataFilter;
            break;
        case 'gallery':
            Control = Gallery;
            break;
        case 'availability-slot-picker':
            Control = AvailabilitySlotPicker;
            break;
        case 'category-field':
            Control = CategoryField;
            break;
        case 'entity-labeler':
            Control = EntityLabeler;
            break;
        case 'tree':
            Control = Tree;
            break;
        case 'table':
            Control = Table;
            break;
        case 'action-input':
            Control = ActionInput;
            break;
        case 'video-input':
            Control = VideoInput;
            break;
        case 'control-group':
            Control = ControlGroup;
            break;
        case 'spacer':
            Control = Spacer;
            break;
        case 'paragraph':
            Control = Paragraph;
            break;
        case 'info':
            Control = Info;
            break;
        case 'block':
            Control = Block;
            break;
        default:
            Control = InputGroup;
            // Adds support to allow custom controls to opt-out of wrapping input group
            if (context.customControls && context.customControls[field.type] && context.customControls[field.type].wrapWithInputGroup === false) {
                Control = context.customControls[field.type];
            }
            break;
    }

    // When in an editing state for this field render the edit state for this fields settings
    if (field.editing) {
        const {editing, ...fieldProps} = field;
        return (
            <>
                {index > 0 ? (<hr className={"border-panels-300"}/>) : null}
                <FieldBuilder
                    // When the fieldset is marked as extendable and the form isn't. This states that the 
                    // fields are only editable and therefor need a reference to the fieldset so that the 
                    // field builder knows which fieldset this field lives in 
                    fieldset={!props.extendable && fieldset.extendable ? fieldset : null}
                    extendable={extendable}
                    userDefinedFieldsetsScope={props.userDefinedFieldsetsScope}
                    editing={editing}
                    field={fieldProps}
                    onActionClick={(e, action, values) => props.onFieldSettingsAction(e, action, values, field, fieldset)}
                />
                {index !== (fieldset.fields.length - 1) ? (<hr className={"border-panels-300"}/>) : null}
            </>
        )
    }

    // Handle when it evaluates to false to hide this field otherwise if it's undefined null or true then
    // show it
    if (evaluate(field.value, field.showsWhen, context.values, context) === false) {
        if (fieldset.editing && field.system === true) {
            // Keep a node mounted for sortable registration without leaving any part of the field visible.
            return <div className="hidden" aria-hidden="true"/>;
        } else if (!fieldset.editing) {
            return null;
        }
    }

    // Handle when the field is a divider and render a hr instead
    if (field.divider === true || _isNumber(field.divider)) {
        const divider = (
            <hr className={classNames(
                "border-panels-300 h-px w-full",
                field.divider === 2 ? "border-b" : null,
                field.divider === 3 ? "border-b-2" : null,
            )}/>
        );
        if (fieldset.sortable && SortableHandle && isSortableField(field)) {
            return (
                <div className={"flex items-center justify-between"}>
                    <DragHandler/>
                    {divider}
                    {fieldset.editing ? (
                        <span
                            className={"shrink-0 pl-2 text-xs font-medium text-primary-500 leading-5 cursor-pointer hover:underline"}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                props.onEditFieldSettings(e, field, fieldset);
                            }}
                        >Edit</span>
                    ) : null}
                </div>
            )
        }
        return divider;
    }

    // Handle when the field is a hidden field and render just the input.
    // In website_form we still render "hidden" fields so they can be edited,
    // except for the internal "type" field which should never show UI.
    if (field.type === 'hidden' && (context.id !== 'website_form' || field.name === 'type')) {
        return (
            <input
                type={field.type}
                name={field.name}
                value={getValue(field, context.values)}
            />
        );
    }

    const fieldProps = {
        ...field,
        // A builder preview is read-only without making the saved field read-only.
        disabled: props.extendable && !props.builder && !field.system ? true : field.disabled,
        editing: fieldset.editing,
        onFieldChange: props.onFieldChange,
        onComponentUpdate: props.onComponentUpdate,
        onEditFieldSettings: (e) => {
            e.preventDefault();
            e.stopPropagation();
            props.onEditFieldSettings(e, field, fieldset);
        }
    };

    if (fieldset.sortable && SortableHandle && isSortableField(field)) {
        return (
            <div className={"cx-sortable-row flex items-center"}>
                <DragHandler/>
                <Control {...fieldProps} className={classNames("grow", fieldProps.className)}/>
            </div>
        )
    } else {
        return (<Control {...fieldProps} />)
    }
}

Fields.propTypes = {
    fieldset: PropTypes.object,
    onFieldChange: PropTypes.func,
    onEditFieldSettings: PropTypes.func,
    onFieldSettingsAction: PropTypes.func,
};

Fields.defaultProps = {
    fieldset: {},
    onFieldChange: () => {
    },
    onEditFieldSettings: () => {
    },
    onFieldSettingsAction: () => {
    }
};

function Fields({fieldset, index: _fieldsetIndex, ...props}) {
    const extendable = getExtendableSettings(fieldset, props.extendable);
    const canAddFieldset = (!props.extendable && fieldset.extendable && fieldset.editing) || (props.extendable && fieldset.extendable && fieldset.editing);
    if (fieldset.childrenOnly && !props.renderChildrenOnlyFields) return null;
    if (!canAddFieldset && !fieldset.fields?.length) return null;

    const FieldComponent = fieldset.sortable && StableSortableField ? StableSortableField : Field;
    let sortableIndex = -1;
    return (
        <>
            {fieldset?.fields?.filter(f => !!f).map((field, index) => {
                let id;
                if (field?.type === 'control-group') id = field.fields.map(f => f.name).join('_');
                if (isSortableField(field)) sortableIndex += 1;
                return (
                    <FieldComponent
                        key={field.divider === true ? `${index}_divider` : `${fieldset.id}_${field.name}_${field.id}_${field.showsWhen}_${id}_${field.editing ? '_editing' : ''}`}
                        collection={isSortableField(field) ? 1 : 0}
                        disabled={!isSortableField(field)}
                        index={isSortableField(field) ? sortableIndex : index}
                        field={field}
                        fieldset={fieldset}
                        {...props}
                    />
                )
            })}
            {canAddFieldset ? (
                <FieldBuilder
                    // When the fieldset is marked as extendable and the form isn't. This states that the fields are
                    // only editable and therefor need a reference to the fieldset so that the field builder knows
                    // which fieldset it should add it 
                    fieldset={!props.extendable && fieldset.extendable ? fieldset : null}
                    extendable={extendable}
                    userDefinedFieldsetsScope={props.userDefinedFieldsetsScope}
                    onActionClick={(e, action, values) => props.onFieldSettingsAction(e, action, values, null, fieldset)}
                />
            ) : null}
            {props.children}
        </>
    );
}

// These wrappers must have stable component identities. Creating them during render remounts every field whenever
// the form context changes, which discards an open field-builder draft and can break sortable registration.
const StableSortableField = SortableElement ? SortableElement(Field) : null;
const StableSortableFields = SortableContainer ? SortableContainer(Fields) : null;

export default ({className, ...props}) => {
    const containerRef = useRef();

    return (
        <div ref={containerRef} className={className}>
            {!(props.fieldset.sortable && StableSortableFields) ? (
                <Fields {...props} />
            ) : (
                <StableSortableFields
                    {...props}
                    useDragHandle
                    getContainer={() => {
                        if (!containerRef.current) return document.body;

                        let parent = containerRef.current.parentElement;
                        while (!(parent.tagName === 'BODY' || parent.scrollHeight > parent.offsetHeight)) {
                            parent = parent.parentElement;
                        }

                        return parent || document.body;
                    }}
                    helperClass={"sort-helper"}
                    onSortStart={() => {
                        const helper = document.getElementsByClassName("sort-helper")[0];
                        if (helper) helper.style.zIndex = 50000;
                        document.body.classList.add('is-sorting');
                    }}
                    onSortEnd={(options) => {
                        document.body.classList.remove('is-sorting');
                        if (!props.onFieldSortEnd) return;
                        return props.onFieldSortEnd({
                            ...options,
                            fieldset: props.fieldset
                        });
                    }}
                />
            )}
        </div>
    )
};
